import React from "react";
import TagBadge from "./TagBadge";
import { Edit, Trash2, Store } from "lucide-react";
import { Shop } from "../types/Shop";
import { getOptimizedShopIcon, generateImageKitSrcSet } from "../utils/imagekit";

interface ShopTableProps {
  shops: Shop[];
  onEdit: (shop: Shop) => void;
  onDelete: (id: string) => void;
}

const ShopTable: React.FC<ShopTableProps> = ({ shops, onEdit, onDelete }) => (
  <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
      <thead className="bg-gray-50 dark:bg-gray-700">
        <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Tags</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Address</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
        </tr>
      </thead>
      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
        {shops.map((shop) => (
          <tr key={shop.id}>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-8 h-8">
                  {shop.iconUrl ? (
                    <img 
                  src={getOptimizedShopIcon.small(shop.iconUrl)} 
                  srcSet={generateImageKitSrcSet(shop.iconUrl, [32, 64])}
                  sizes="32px"
                  alt={`${shop.name} logo`}
                  className="w-8 h-8 rounded object-cover border border-gray-200 dark:border-gray-600"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                    if (nextElement) {
                      nextElement.style.display = 'flex';
                    }
                  }}
                />
                  ) : null}
                  <div 
                    className={`w-8 h-8 rounded border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 flex items-center justify-center ${shop.iconUrl ? 'hidden' : 'flex'}`}
                    title={shop.iconUrl ? undefined : "No logo"}
                  >
                    <Store size={14} className="text-gray-400 dark:text-gray-500" />
                  </div>
                </div>
                <span className="truncate">{shop.name}</span>
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
              <span className="capitalize">{shop.type.replace("_", " ")}</span>
            </td>
            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-300">
              <div className="flex flex-wrap gap-1">
                {shop.tags?.map((tag) => (
                  <TagBadge key={tag} tag={tag} />
                ))}
                {(!shop.tags || shop.tags.length === 0) && <span className="text-gray-400 dark:text-gray-500 text-xs">No tags</span>}
              </div>
            </td>
            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-300">{shop.address}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
              <div className="flex space-x-2">
                <button onClick={() => onEdit(shop)} className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300">
                  <Edit size={16} />
                </button>
                <button onClick={() => shop.id && onDelete(shop.id)} className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300">
                  <Trash2 size={16} />
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default ShopTable;
